// Stripe Checkout Service (authenticated buy-more-licenses flow)
// ---------------------------------------------------------------
// Client Owner-only surface that lets an already-signed-up tenant purchase
// additional pentest entitlements ("app licenses"). Two calls:
//   * fetchPurchasablePlans() — the region-wide plan catalog for display (no
//     Stripe price ids; the embedded checkout shows the actual amount).
//   * initiateEntitlementCheckout(planId, quantity) — opens an embedded Stripe
//     Checkout Session for the caller's OWN tenant. The BE derives the tenant
//     from the JWT (never input) and creates entitlements only on the webhook's
//     verified payment-success event.
//
// Query + mutation strings are defined here (not in the auto-generated
// queries.ts / mutations.ts, which get overwritten by codegen) so this
// hand-wired surface is stable.

import { API, graphqlOperation as _graphqlOperation } from "aws-amplify";
import { assertNoTenantIdInVariables } from "@/services/graphql/assertNoTenantId";
import { getGraphData } from "@/utils/transform";

// Mirror of the BE plan-catalog projection (handlers/billingdatasource._list_plans).
// Carries the live per-app amount but deliberately NO Stripe price id.
export interface PurchasablePlan {
  planId: string;
  displayName: string;
  planType: "one_off" | "subscription";
  tier: string;
  testsPerPeriod: number;
  // Live price resolved server-side from the same Stripe Price the checkout
  // charges against, so the amount shown is the amount billed. Null when Stripe
  // could not be reached — render the plan without a price, never a guess.
  unitAmount: number | null;   // minor units (e.g. cents)
  currency: string | null;
  interval: "month" | "year" | null;  // null for one-off
  intervalCount: number | null;
}

// ---------------------------------------------------------------------------
// Price display
// ---------------------------------------------------------------------------
// Amounts arrive in MINOR units from Stripe (the same Price the checkout bills
// against — see the BE's _resolve_plan_prices). Formatting lives here so the
// Billing page and anything added later can't format the same plan two ways.
//
// There is no hard-coded price anywhere in this app on purpose: Stripe is the
// single source, so a repricing there flows to this page with no code change.

const MINOR_UNITS_PER_MAJOR = 100;
const MONTHS_PER_YEAR = 12;

/** Whole-dollar display; prices are round. Falls back to a plain "$" prefix for
 *  a currency code Intl doesn't recognize. */
export function formatPlanAmount(minorUnits: number, currency: string): string {
  const majorUnits = minorUnits / MINOR_UNITS_PER_MAJOR;
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency.toUpperCase(),
      maximumFractionDigits: 0,
    }).format(majorUnits);
  } catch {
    return `$${Math.round(majorUnits).toLocaleString("en-US")}`;
  }
}

/** "$1,500/mo", "$10,800/yr", "$2,000 one-time" — or null when the plan came
 *  back unpriced (Stripe unreachable), so callers can omit the line entirely
 *  rather than print a placeholder. */
export function formatPlanPrice(plan: PurchasablePlan): string | null {
  if (plan.unitAmount == null || !plan.currency) return null;
  const amount = formatPlanAmount(plan.unitAmount, plan.currency);
  if (plan.interval === "month") return `${amount}/mo`;
  if (plan.interval === "year") return `${amount}/yr`;
  return `${amount} one-time`;
}

// ---------------------------------------------------------------------------
// Plan presentation
// ---------------------------------------------------------------------------
// Labels and descriptions are derived from (planType, interval) rather than
// read from the catalog row's displayName, for two reasons:
//
//   1. Branding. These are the customer-facing tier names and they MUST match
//      the marketing site and the sign-up portal word for word (see
//      nimis-intelligence-customer-portal/frontend/lib/constants.ts PLAN_OPTIONS).
//      Deriving them here means the dashboard cannot drift from the portal
//      because someone edited a DynamoDB row.
//   2. Catalog rows carry operational names ("Monthly Subscription", and on a
//      dev tenant "Dev Monthly (30)") that were never meant to face a customer.
//
// The catalog still owns which plans EXIST and their cadence; this only decides
// how a plan is described.

export type PlanPresentation = { label: string; description: string };

const POINT_IN_TIME: PlanPresentation = {
  label: "Point-in-Time",
  description: "A single scheduled pentest, plus a second test to confirm your fixes.",
};
const CONTINUOUS_MONTHLY: PlanPresentation = {
  label: "Continuous (Monthly)",
  description: "Continuous testing, two pentests every month. Billed monthly, no lock-in, cancel anytime.",
};
const CONTINUOUS_ANNUAL: PlanPresentation = {
  label: "Continuous (Annual)",
  description: "Continuous testing, two pentests every month. Billed once a year, up front.",
};
// A subscription whose cadence we could not resolve. Rendering it as monthly
// would be a guess, and when two plans guess the same way they become
// indistinguishable in the list — which is exactly the bug this avoids.
const CONTINUOUS_UNKNOWN_CADENCE: PlanPresentation = {
  label: "Continuous",
  description: "Continuous testing, two pentests every month.",
};

/** Customer-facing name and blurb for a plan. The annual description gets the
 *  savings clause appended by the caller, which knows the live figure. */
export function planPresentation(plan: PurchasablePlan): PlanPresentation {
  if (plan.planType === "one_off") return POINT_IN_TIME;
  if (plan.interval === "year") return CONTINUOUS_ANNUAL;
  if (plan.interval === "month") return CONTINUOUS_MONTHLY;
  return CONTINUOUS_UNKNOWN_CADENCE;
}

/** The per-web-app price, stated in full: "$1,500 per web app, per month".
 *  Null when the plan came back unpriced.
 *
 *  Spelled out rather than compressed to "$1,500/mo" because every plan is
 *  priced PER WEB APP and a bare rate reads as the whole bill. The order
 *  summary below it then gives the total for the chosen quantity. */
export function formatPerAppPrice(plan: PurchasablePlan): string | null {
  if (plan.unitAmount == null || !plan.currency) return null;
  const amount = formatPlanAmount(plan.unitAmount, plan.currency);
  if (plan.interval === "month") return `${amount} per web app, per month`;
  if (plan.interval === "year") return `${amount} per web app, per year`;
  return `${amount} per web app, one-time`;
}

/** Annual savings versus paying monthly for a year, per web app, as a display
 *  string ("$7,200") — or null when either plan is unpriced, the currencies
 *  differ, or annual isn't actually cheaper.
 *
 *  Computed from the two live prices rather than hard-coded: it matches the
 *  marketing site's formula (MONTHLY * 12 - ANNUAL) and, unlike the portal's
 *  ANNUAL_SAVINGS_USD constant, cannot go stale the day either price changes
 *  in Stripe. */
export function formatAnnualSavings(
  monthlyPlan: PurchasablePlan | undefined,
  annualPlan: PurchasablePlan | undefined,
): string | null {
  if (!monthlyPlan?.unitAmount || !annualPlan?.unitAmount) return null;
  if (!monthlyPlan.currency || monthlyPlan.currency !== annualPlan.currency) return null;
  const savings = monthlyPlan.unitAmount * MONTHS_PER_YEAR - annualPlan.unitAmount;
  if (savings <= 0) return null;
  return formatPlanAmount(savings, annualPlan.currency);
}

/** Plain-English summary of what the user is about to buy, shown BEFORE
 *  checkout opens. Returns null when the plan is unpriced.
 *
 *  This exists because Stripe's own line item renders an annual subscription as
 *  its per-month equivalent ("$1,800.00 / month billed annually" for two apps),
 *  which reads as a monthly charge. The user should never meet the amount for
 *  the first time on the card form, so we state it ourselves: the comparable
 *  per-app rate first, then the figure that actually hits the card.
 *
 *  The annual per-month rate is only quoted when the yearly price divides
 *  evenly into twelve. Otherwise a rounded rate would not reconcile with the
 *  total sitting next to it, and two numbers that don't add up read as an
 *  error even when both are right. */
export function formatOrderSummary(
  plan: PurchasablePlan,
  quantity: number
): string | null {
  if (plan.unitAmount == null || !plan.currency) return null;

  const appCount = `${quantity} web app${quantity === 1 ? "" : "s"}`;
  const total = formatPlanAmount(plan.unitAmount * quantity, plan.currency);

  if (plan.interval === "year") {
    const dividesEvenly = plan.unitAmount % MONTHS_PER_YEAR === 0;
    const perAppRate = dividesEvenly
      ? `${formatPlanAmount(plan.unitAmount / MONTHS_PER_YEAR, plan.currency)} per app per month, billed yearly`
      : `${formatPlanAmount(plan.unitAmount, plan.currency)} per app per year`;
    return `${appCount}, ${perAppRate}. Total ${total} today.`;
  }

  if (plan.interval === "month") {
    const perAppRate = formatPlanAmount(plan.unitAmount, plan.currency);
    return `${appCount}, ${perAppRate} per app per month. Total ${total} per month.`;
  }

  return `${appCount}, ${formatPlanAmount(plan.unitAmount, plan.currency)} per app. Total ${total} today.`;
}

export interface EntitlementCheckoutSession {
  sessionId: string | null;
  clientSecret: string | null;
  publishableKey: string | null;
  // Non-null on failure (FORBIDDEN | ACCOUNT_NOT_ACTIVE | INVALID_PLAN |
  // INVALID_QUANTITY | PLAN_NOT_PURCHASABLE | CHECKOUT_FAILED).
  error: string | null;
}

const LIST_PLANS_QUERY = `query ListPlans {
  listPlans {
    planId
    displayName
    planType
    tier
    testsPerPeriod
    unitAmount
    currency
    interval
    intervalCount
  }
}`;

const CREATE_ENTITLEMENT_CHECKOUT_SESSION = `mutation CreateEntitlementCheckoutSession($input: CreateEntitlementCheckoutSessionInput!) {
  createEntitlementCheckoutSession(input: $input) {
    sessionId
    clientSecret
    publishableKey
    error
  }
}`;

// Same wrapper the other authenticated services use: forces Cognito user-pools
// auth (id token) and asserts the client never sends a tenant id.
const graphqlOperation = (query: any, variables?: any) => {
  assertNoTenantIdInVariables(variables);
  return {
    ..._graphqlOperation(query, variables),
    authMode: "AMAZON_COGNITO_USER_POOLS" as const,
  };
};

interface GraphQLResponse {
  data?: Record<string, unknown>;
  errors?: Array<{ message?: string }>;
}

export async function fetchPurchasablePlans(): Promise<PurchasablePlan[]> {
  const response = (await API.graphql(
    graphqlOperation(LIST_PLANS_QUERY)
  )) as GraphQLResponse;
  const plans = getGraphData(response, "listPlans");
  return Array.isArray(plans) ? (plans as PurchasablePlan[]) : [];
}

export async function initiateEntitlementCheckout(
  planId: string,
  quantity: number
): Promise<EntitlementCheckoutSession> {
  try {
    const response = (await API.graphql(
      graphqlOperation(CREATE_ENTITLEMENT_CHECKOUT_SESSION, {
        input: { planId, quantity },
      })
    )) as GraphQLResponse;
    const result = getGraphData(response, "createEntitlementCheckoutSession") as
      | EntitlementCheckoutSession
      | null;
    if (!result) {
      return { sessionId: null, clientSecret: null, publishableKey: null, error: "CHECKOUT_FAILED" };
    }
    return result;
  } catch (checkoutError: any) {
    const message =
      checkoutError?.errors?.[0]?.message ||
      checkoutError?.message ||
      "CHECKOUT_FAILED";
    return { sessionId: null, clientSecret: null, publishableKey: null, error: message };
  }
}

// User-facing copy for each BE error code returned by the checkout mutation.
export function checkoutErrorMessage(code: string | null | undefined): string {
  switch (code) {
    case "FORBIDDEN":
      return "Only an account owner can buy licenses.";
    case "ACCOUNT_NOT_ACTIVE":
      return "Your account isn't active. Contact support if you believe this is an error.";
    case "INVALID_PLAN":
    case "PLAN_NOT_PURCHASABLE":
      return "That plan isn't available to purchase right now.";
    case "INVALID_QUANTITY":
      return "Please choose a valid quantity.";
    default:
      return "We couldn't start checkout. Please try again.";
  }
}
